import { useIntl } from '@leagueplatform/locales';
import {
  Box,
  FormField,
  StackLayout,
  TextInput,
  queryHelpers,
  useMediaQuery,
} from '@leagueplatform/genesis-core';
import { SectionHeading } from 'components/section-heading/section-heading.component';
import { type ContactField } from './communication-preferences.types';
import { type ContactErrors } from './use-communication-preferences-form.hook';

export const ContactInformationSection = ({
  contact,
  errors,
  onChange,
}: {
  contact: Record<ContactField, string>;
  errors: ContactErrors;
  onChange: (field: ContactField, value: string) => void;
}) => {
  const { formatMessage } = useIntl();
  const isBelowTablet = useMediaQuery(queryHelpers.down('tablet'));
  const headingId = 'comm-pref-contact-heading';

  return (
    <StackLayout
      spacing="$one"
      horizontalAlignment="stretch"
      role="group"
      aria-labelledby={headingId}
    >
      <SectionHeading id={headingId}>
        {formatMessage({ id: 'STR_COMM_PREF_CONTACT_INFORMATION' })}
      </SectionHeading>
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: isBelowTablet ? '1fr' : '1fr 1fr',
          gap: '$one',
        }}
      >
        <FormField
          id="comm-pref-mobile-phone"
          label={formatMessage({ id: 'STR_MOBILE_PHONE_NUMBER' })}
          hint={formatMessage({ id: 'STR_COMM_PREF_MOBILE_PHONE_HINT' })}
          inputStatus={errors.mobilePhone ? 'error' : undefined}
          statusIconLabel={formatMessage({ id: 'STR_ERROR' })}
          statusMessage={
            errors.mobilePhone && formatMessage({ id: errors.mobilePhone })
          }
        >
          <TextInput
            type="tel"
            value={contact.mobilePhone}
            onChange={(event) => onChange('mobilePhone', event.target.value)}
          />
        </FormField>
        <FormField
          id="comm-pref-preferred-phone"
          label={formatMessage({ id: 'STR_PREFERRED_PHONE_NUMBER' })}
          hint={formatMessage({ id: 'STR_COMM_PREF_PREFERRED_PHONE_HINT' })}
          inputStatus={errors.preferredPhone ? 'error' : undefined}
          statusIconLabel={formatMessage({ id: 'STR_ERROR' })}
          statusMessage={
            errors.preferredPhone &&
            formatMessage({ id: errors.preferredPhone })
          }
        >
          <TextInput
            type="tel"
            value={contact.preferredPhone}
            onChange={(event) => onChange('preferredPhone', event.target.value)}
          />
        </FormField>
        <Box css={{ gridColumn: isBelowTablet ? 'auto' : '1 / -1' }}>
          <FormField
            id="comm-pref-email"
            label={formatMessage({ id: 'STR_EMAIL_ADDRESS' })}
            inputStatus={errors.email ? 'error' : undefined}
            statusIconLabel={formatMessage({ id: 'STR_ERROR' })}
            statusMessage={errors.email && formatMessage({ id: errors.email })}
          >
            <TextInput
              type="email"
              value={contact.email}
              onChange={(event) => onChange('email', event.target.value)}
            />
          </FormField>
        </Box>
      </Box>
    </StackLayout>
  );
};
